import { createSelector } from '@reduxjs/toolkit';
import { isEqual } from 'lodash-es';

import { IAppliedRewards } from 'state/loyalty/hooks/types';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { parseStringifiedJSON } from 'utils/parse-string';

import { IRewardsState } from './rewards.types';

export const getAppliedRewardsFromStorage = () => {
  const storedAppliedRewards = LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_REWARDS);
  return parseStringifiedJSON({ value: storedAppliedRewards, defaultValue: {} }) || {};
};

export const updateAppliedRewardsInStorage = (appliedReward: IAppliedRewards) => {
  LocalStorage.setItem(StorageKeys.APPLIED_LOYALTY_REWARDS, JSON.stringify(appliedReward));
};

export const removeAppliedRewardsInStorage = () => {
  LocalStorage.removeItem(StorageKeys.APPLIED_LOYALTY_REWARDS);
};

export const computeTotalTimesRewardApplied = (
  appliedRewards: IAppliedRewards
): Record<string, number> => {
  return Object.values(appliedRewards).reduce(
    (acc: Record<string, number>, { rewardId, timesApplied }) => {
      if (timesApplied) {
        acc[rewardId] = (acc[rewardId] || 0) + timesApplied;
      }
      return acc;
    },
    {}
  );
};

export const setStateFromAppliedRewards = (
  state: IRewardsState,
  appliedRewards: IAppliedRewards
) => {
  updateAppliedRewardsInStorage(appliedRewards);
  if (!isEqual(state.appliedLoyaltyRewards, appliedRewards)) {
    state.appliedLoyaltyRewards = appliedRewards;
  }
};

export const getAppliedPointsInCart = createSelector(
  (state: IRewardsState) => state.appliedLoyaltyRewards,
  (state: IRewardsState) => state.availableLoyaltyRewardsMap,
  (appliedLoyaltyRewards, availableLoyaltyRewardsMap) => {
    return Object.values(appliedLoyaltyRewards).reduce((acc, { rewardBenefitId, timesApplied }) => {
      const { pointCost } = availableLoyaltyRewardsMap[rewardBenefitId] || {};
      return acc + (pointCost || 0) * timesApplied;
    }, 0);
  }
);

export const getStagedCartPoints = (state: IRewardsState, basePoints: number) => {
  const appliedPointsInCart = getAppliedPointsInCart(state);
  const availablePointsToUse = basePoints - appliedPointsInCart;
  const isValid = 0 <= availablePointsToUse && availablePointsToUse <= basePoints;
  if (isValid) {
    return availablePointsToUse;
  }
  // TODO: on the else case, analyze if we need to reset some props if we find a discrepancy
  return null;
};
